import { z } from 'zod';
import { timestampSchema } from './common.schemas';

/**
 * Agent System Schemas
 */

// Agent types
export const agentTypeEnum = z.enum([
  'orchestrator',
  'listener',
  'resolver',
  'technical',
  'billing',
  'product',
  'anticipator',
]);

// Agent action schema
export const agentActionSchema = z
  .object({
    id: z.string().uuid(),
    agentType: agentTypeEnum,
    action: z.string(),
    status: z.enum(['pending', 'in_progress', 'completed', 'failed']),
    input: z.record(z.unknown()).nullable(),
    output: z.record(z.unknown()).nullable(),
    toolName: z.string().nullable(),
    toolParams: z.record(z.unknown()).nullable(),
    toolResult: z.record(z.unknown()).nullable(),
    reasoning: z.string().nullable(), // XAI explanation
    tokenCount: z.number().int().nonnegative(),
    cost: z.number().nonnegative(),
    duration: z.number().int().nullable(), // milliseconds
    error: z.string().nullable(),
    conversationId: z.string().uuid(),
    completedAt: z.coerce.date().nullable(),
  })
  .merge(timestampSchema);

// Agent status
export const agentStatusSchema = z.object({
  agentType: agentTypeEnum,
  status: z.enum(['active', 'idle', 'error']),
  currentLoad: z.number().int().nonnegative(),
  actionsPerHour: z.number().int().nonnegative(),
  averageResponseTime: z.number().nonnegative(), // milliseconds
  errorRate: z.number().min(0).max(100), // percentage
});

// Agent configuration
export const agentConfigSchema = z.object({
  agentType: agentTypeEnum,
  enabled: z.boolean(),
  maxConcurrent: z.number().int().positive().default(10),
  timeout: z.number().int().positive().default(30000), // milliseconds
  retryAttempts: z.number().int().nonnegative().default(3),
  llmModel: z.string().default('gpt-4o-mini'),
  temperature: z.number().min(0).max(2).default(0.7),
  systemPrompt: z.string().optional(),
  tools: z.array(z.string()).optional(),
});

// Agent performance metrics
export const agentMetricsSchema = z.object({
  agentType: agentTypeEnum,
  totalActions: z.number().int().nonnegative(),
  successfulActions: z.number().int().nonnegative(),
  failedActions: z.number().int().nonnegative(),
  averageDuration: z.number().nonnegative(),
  totalCost: z.number().nonnegative(),
  totalTokens: z.number().int().nonnegative(),
  period: z.object({
    from: z.coerce.date(),
    to: z.coerce.date(),
  }),
});

// Live agentic system status
export const agenticSystemStatusSchema = z.object({
  orchestratorLoad: z.number().min(0).max(100), // percentage
  resolverActionsPerHour: z.number().int().nonnegative(),
  agents: z.array(
    z.object({
      type: agentTypeEnum,
      status: z.enum(['active', 'idle']),
    }),
  ),
});

// Export inferred types
export type AgentType = z.infer<typeof agentTypeEnum>;
export type AgentAction = z.infer<typeof agentActionSchema>;
export type AgentStatus = z.infer<typeof agentStatusSchema>;
export type AgentConfig = z.infer<typeof agentConfigSchema>;
export type AgentMetrics = z.infer<typeof agentMetricsSchema>;
export type AgenticSystemStatus = z.infer<typeof agenticSystemStatusSchema>;
